import React from "react";
import { InfoHint } from "components/common/InfoHint";
import { plural } from "components/common/ImpactRow";
import { TooltipButton } from "components/common/TooltipButton";
import { formatDayMonthYear } from "@/lib/dates";
import {
  formatSeatMoney,
  PLUS_TAX,
  type SeatRate,
} from "components/main/organizations/seatRate";
import type {
  CatalogPlan,
  CurrentPlan,
  Org,
  PaymentStanding,
} from "@/lib/api/organizations";

export const PlanSummary: React.FC<{
  org: Org;
  plan: CurrentPlan | null;
  /// `null` where the subscription's price is unknown in its own currency.
  seatRate: SeatRate | null;
  planRead: "loading" | "loaded" | "failed";
  currentRow: CatalogPlan | null;
  memberCount: number;
  standing: PaymentStanding["state"];
  pendingCancel: boolean;
  paused: boolean;
  cycleDate: string | null;
  offersUpgrade: boolean;
  canUpgrade: boolean;
  onUpgrade: () => void;
}> = ({
  org,
  plan,
  seatRate,
  planRead,
  currentRow,
  memberCount,
  standing,
  pendingCancel,
  paused,
  cycleDate,
  offersUpgrade,
  canUpgrade,
  onUpgrade,
}) => {
  const rate = seatRate ? formatSeatMoney(seatRate) : null;

  /// The strongest thing true of the plan, worst first.
  const planState: { label: string; tone: string } | null =
    standing !== "current"
      ? { label: "Past due", tone: "text-mono-small-error" }
      : pendingCancel
        ? {
            label: cycleDate ? `Ends ${cycleDate}` : "Ends this cycle",
            tone: "text-mono-small-warning",
          }
        : cycleDate
          ? { label: `Renews ${cycleDate}`, tone: "text-mono-small-muted" }
          : null;

  /// Paused first: the trail closes the period the same way for a pause and an
  /// end, so only the mirror can tell them apart.
  const planNote = paused
    ? `Your ${plan?.previous?.name ?? "paid"} subscription is paused. This ` +
      `organization is on ${plan?.name ?? "the free plan"} until it resumes.`
    : plan?.previous
      ? plan.previous.reason === "ended"
        ? `Your ${plan.previous.name} plan ended on ${formatDayMonthYear(plan.previous.ended_at)}.`
        : `Your plan changed from ${plan.previous.name} to ${plan.name} on ${formatDayMonthYear(plan.previous.ended_at)}.`
      : null;

  /// The catalog's answer; a period's `limits.seats` is already multiplied.
  const perSeatTier = Boolean(
    currentRow && currentRow.limits.seats === 1 && currentRow.price_cents > 0,
  );
  const rateBasis = plan
    ? (perSeatTier ? `/seat · ${plan.billing_period}` : plan.billing_period) +
      (seatRate?.taxOnTop ? `, ${PLUS_TAX}` : "")
    : "";

  /// The period's cap, never the subscription's quantity: the invite door
  /// enforces the period, which lags a purchase until its webhook.
  const seatsTotal = plan?.limits.seats ?? null;
  const vacantSeats = seatsTotal === null ? null : seatsTotal - memberCount;
  /// Silent when exactly full; "over" happens (a zeroed cap, a downgrade).
  const seatsVacant =
    vacantSeats === null || org.is_personal || vacantSeats === 0
      ? null
      : vacantSeats > 0
        ? {
            label: `${vacantSeats.toLocaleString()} vacant`,
            tone: "text-fg-muted-regular",
          }
        : {
            label: `${(-vacantSeats).toLocaleString()} over`,
            tone: "text-warning",
          };

  const seatBilling =
    (perSeatTier && rate
      ? `Seats are bought, not used up: each costs ${rate} ${rateBasis}, filled or not.\n`
      : "Seats are bought, not used up.\n") +
    "Every member and every pending invitation holds one. Removing a member vacates their seat and leaves the bill where it is." +
    (vacantSeats !== null && vacantSeats <= 0
      ? "\nEvery seat is taken — add one to invite anyone else."
      : "");

  return (
    // Renders whether or not a period could be read: an org without one still
    // has a tier to buy.
    <div className="flex flex-col px-2 py-1">
      <div className="flex min-h-8 items-center gap-3">
        <span className="flex min-w-0 flex-1 items-center gap-2">
          {plan ? (
            <span className="flex min-w-0 flex-col">
              <span className="text-fg-muted-regular-small">Current plan</span>
              <span className="flex min-w-0 items-baseline gap-2">
                <span className="min-w-0 truncate text-fg-title">
                  {plan.name}
                </span>
                {planState && (
                  <span className={`shrink-0 ${planState.tone}`}>
                    {planState.label}
                  </span>
                )}
              </span>
              {planNote && (
                <span
                  className="text-fg-muted-regular-small"
                  data-testid="plan-note"
                >
                  {planNote}
                </span>
              )}
            </span>
          ) : (
            <span className="min-w-0 text-fg-muted-regular-small">
              {planRead === "loaded"
                ? "No plan period recorded for this organization."
                : planRead === "failed"
                  ? "The plan couldn't be loaded right now."
                  : "Loading plan…"}
            </span>
          )}
        </span>
        {offersUpgrade && (
          <span className="flex shrink-0 items-center gap-2">
            {!canUpgrade && (
              <span
                className="text-fg-muted-regular-small"
                data-testid="upgrade-owner-only"
              >
                Only an owner can change this plan.
              </span>
            )}
            <TooltipButton
              title="See what the higher plans include."
              disableTooltip={!canUpgrade}
              variant="primary"
              size="sm"
              disabled={!canUpgrade}
              onClick={onUpgrade}
              data-testid="open-plans"
            >
              Upgrade
            </TooltipButton>
          </span>
        )}
      </div>
      {plan && (
        <div className="flex flex-col gap-1">
          {rate && (
            <span className="flex items-baseline gap-2" data-testid="plan-rate">
              <span className="text-figure">{rate}</span>
              <span className="text-fg-muted-regular">{rateBasis}</span>
            </span>
          )}
          <span className="flex items-center gap-2" data-testid="plan-terms">
            {seatsTotal !== null && !org.is_personal ? (
              <>
                <span className="inline-flex items-center gap-1">
                  <span className="text-figure">
                    {`${seatsTotal.toLocaleString()} ${plural(seatsTotal, "seat")}`}
                  </span>
                  <InfoHint title={seatBilling} label="How seats work" />
                </span>
                {seatsVacant && (
                  <span className={seatsVacant.tone} data-testid="seats-vacant">
                    {seatsVacant.label}
                  </span>
                )}
              </>
            ) : null}
          </span>
        </div>
      )}
    </div>
  );
};
